import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useInvoice, useUpdateInvoiceStatus, useRecordPayment, useUpdateInvoice, useClients, useCreateCreditNote, useCancelInvoice, useIssueInvoice, useSendInvoiceEmail, useSendInvoiceViaPeppol, downloadInvoicePdf, openInvoicePdfForPrint } from '@/hooks/useApi';
import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import PageSkeleton from '@/components/PageSkeleton';

const LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'fr', label: 'Français' },
  { code: 'nl', label: 'Nederlands' },
  { code: 'es', label: 'Español' },
];

const STATUS_COLORS: Record<string, string> = {
  draft: 'badge-gray', sent: 'badge-blue', viewed: 'badge-blue',
  partially_paid: 'badge-yellow', paid: 'badge-green',
  overdue: 'badge-red', cancelled: 'badge-gray', credited: 'badge-gray',
};

const TRANSITIONS: Record<string, string[]> = {
  sent: ['paid'],
  viewed: ['paid'],
  partially_paid: ['paid'],
  overdue: ['paid'],
};

const VAT_RATES = ['21', '12', '6', '0'];

interface LineItem {
  description: string;
  quantity: number;
  unitPrice: string;
  vatRate: string;
}

export default function InvoiceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { data: invoice, isLoading } = useInvoice(id!);
  const updateStatus = useUpdateInvoiceStatus();
  const recordPayment = useRecordPayment();
  const createCreditNote = useCreateCreditNote();
  const cancelInvoice = useCancelInvoice();
  const issueInvoice = useIssueInvoice();
  const updateInvoice = useUpdateInvoice();
  const sendInvoiceEmail = useSendInvoiceEmail();
  const sendInvoiceViaPeppol = useSendInvoiceViaPeppol();
  const [showPayment, setShowPayment] = useState(false);
  const [showCreditNote, setShowCreditNote] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showPaidConfirm, setShowPaidConfirm] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showSendModal, setShowSendModal] = useState(false);
  const [sendLanguage, setSendLanguage] = useState('en');
  const [creditReason, setCreditReason] = useState('');
  const [creditLines, setCreditLines] = useState<LineItem[]>([]);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('bank_transfer');

  // Edit mode state
  const [editing, setEditing] = useState(false);
  const [clientId, setClientId] = useState('');
  const [issueDate, setIssueDate] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<LineItem[]>([]);

  const { data: clientsData } = useClients(editing ? {} : { enabled: false });

  useEffect(() => {
    if (!invoice) return;

    const isEditMode = location.pathname.endsWith('/edit');
    if (isEditMode && invoice.status === 'draft') {
      setEditing(true);
      return;
    }

    setEditing(false);
  }, [location.pathname, invoice]);

  // Populate form state when entering edit mode
  useEffect(() => {
    if (editing && invoice) {
      setClientId(invoice.client?.id || '');
      setIssueDate(invoice.issueDate || '');
      setDueDate(invoice.dueDate || '');
      setNotes(invoice.notes || '');
      setLines(
        invoice.lines?.map((l) => ({
          description: l.description,
          quantity: Number(l.quantity),
          unitPrice: String(l.unitPrice),
          vatRate: String(Number(l.vatRate)),
        })) || [{ description: '', quantity: 1, unitPrice: '', vatRate: '21' }]
      );
    }
  }, [editing, invoice]);

  if (isLoading) return <PageSkeleton variant="detail" />;
  if (!invoice) return <p className="text-gray-400 py-12 text-center">Invoice not found</p>;

  const isDraft = invoice.status === 'draft';
  const canCancel = ['draft', 'sent', 'viewed', 'overdue'].includes(invoice.status);
  const transitions = TRANSITIONS[invoice.status] || [];

  function handleCancel() {
    cancelInvoice.mutate(invoice!.id, {
      onSuccess: (result) => {
        setShowCancelConfirm(false);
        if (!result) {
          // Draft was deleted (204 No Content)
          toast.success(t('invoices.cancel.draftDeleted'));
          navigate('/invoices');
        } else {
          // Issued invoice cancelled with credit note
          toast.success(
            t('invoices.cancel.creditNoteCreated', {
              number: result.creditNote?.creditNoteNumber,
            })
          );
        }
      },
      onError: () => {
        setShowCancelConfirm(false);
      },
    });
  }

  function handleStatusChange(status: string) {
    if (status === 'paid') {
      setShowPaidConfirm(true);
      return;
    }

    updateStatus.mutate(
      { id: invoice!.id, status },
      { onSuccess: () => toast.success(t('common.saved')) }
    );
  }

  function confirmPaidStatus() {
    updateStatus.mutate(
      { id: invoice!.id, status: 'paid' },
      {
        onSuccess: () => {
          setShowPaidConfirm(false);
          toast.success(t('common.saved'));
        },
        onError: () => {
          setShowPaidConfirm(false);
          toast.error(t('common.error'));
        },
      }
    );
  }

  function handleRecordPayment(e: React.FormEvent) {
    e.preventDefault();
    recordPayment.mutate(
      { id: invoice!.id, amount: paymentAmount, method: paymentMethod },
      {
        onSuccess: () => {
          toast.success(t('invoices.paymentRecorded'));
          setShowPayment(false);
          setPaymentAmount('');
        },
      }
    );
  }

  // ── Edit helpers ──
  function addLine() {
    setLines([...lines, { description: '', quantity: 1, unitPrice: '', vatRate: '21' }]);
  }

  function removeLine(idx: number) {
    if (lines.length <= 1) return;
    setLines(lines.filter((_, i) => i !== idx));
  }

  function updateLine(idx: number, field: keyof LineItem, value: string | number) {
    setLines(lines.map((l, i) => (i === idx ? { ...l, [field]: value } : l)));
  }

  function lineTotal(l: LineItem) {
    const net = l.quantity * Number(l.unitPrice || 0);
    return { net, vat: net * (Number(l.vatRate) / 100), gross: net * (1 + Number(l.vatRate) / 100) };
  }

  const editTotals = lines.reduce(
    (a, l) => { const lt = lineTotal(l); return { net: a.net + lt.net, vat: a.vat + lt.vat, gross: a.gross + lt.gross }; },
    { net: 0, vat: 0, gross: 0 }
  );

  function handleSave(e: React.FormEvent) {
    e.preventDefault();
    updateInvoice.mutate(
      {
        id: invoice!.id,
        clientId,
        issueDate,
        dueDate,
        notes,
        lines: lines.map((l) => ({
          description: l.description,
          quantity: l.quantity,
          unitPrice: l.unitPrice,
          vatRate: l.vatRate,
        })),
      },
      {
        onSuccess: () => {
          navigate(`/invoices/${invoice!.id}`, { replace: true });
        },
      }
    );
  }

  function handleSaveVia(channel: 'email' | 'whatsapp' | 'peppol') {
    if (channel === 'email') {
      setSendLanguage((invoice as any).client?.language || i18n.language?.split('-')[0] || 'en');
      setShowSaveModal(false);
      setShowSendModal(true);
      return;
    }

    if (channel === 'peppol') {
      const sendingId = toast.loading(t('invoices.peppolSending'));
      sendInvoiceViaPeppol.mutate(invoice!.id, {
        onSuccess: () => {
          toast.dismiss(sendingId);
          toast.success(t('invoices.peppolSent'));
          setShowSaveModal(false);
        },
        onError: (err: unknown) => {
          toast.dismiss(sendingId);
          const detail =
            (err as { response?: { data?: { error?: { detail?: string } } } })?.response?.data?.error?.detail;
          const message = detail && /not reachable/i.test(detail)
            ? t('invoices.peppolNotReachable')
            : detail
              ? `${t('invoices.peppolError')}: ${detail}`
              : t('invoices.peppolError');
          toast.error(message);
        },
      });
      return;
    }

    toast(t('invoices.channelUnavailable'));
    setShowSaveModal(false);
  }

  function handleSaveDraft() {
    issueInvoice.mutate(invoice!.id, {
      onSuccess: () => {
        toast.success(t('common.saved'));
        setShowSaveModal(true);
      },
      onError: () => toast.error(t('common.error')),
    });
  }

  // ═══════════════════════════════════════════════
  // EDIT MODE
  // ═══════════════════════════════════════════════
  if (editing) {
    return (
      <div>
        <div className="flex items-center gap-3 mb-6">
          <button onClick={() => navigate(`/invoices/${invoice.id}`)} className="text-gray-400 hover:text-gray-600">←</button>
          <h1 className="text-2xl font-bold">{t('invoices.editInvoice', 'Edit Invoice')}</h1>
          <span className="badge badge-gray">{invoice.invoiceNumber || t('invoices.draft')}</span>
        </div>

        <form onSubmit={handleSave}>
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            <div className="lg:col-span-3 space-y-6">
              {/* Header fields */}
              <div className="card card-body grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="label">{t('invoices.client')} *</label>
                  <select className="input" value={clientId} onChange={(e) => setClientId(e.target.value)} required>
                    <option value="">{t('invoices.selectClient')}</option>
                    {clientsData?.data?.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="label">{t('invoices.date')} *</label>
                  <input type="date" className="input" value={issueDate} onChange={(e) => setIssueDate(e.target.value)} required />
                </div>
                <div>
                  <label className="label">{t('invoices.dueDate')} *</label>
                  <input type="date" className="input" value={dueDate} onChange={(e) => setDueDate(e.target.value)} required />
                </div>
              </div>

              {/* Lines */}
              <div className="card">
                <div className="card-header flex items-center justify-between">
                  <h3 className="font-semibold">{t('invoices.lines')}</h3>
                  <button type="button" className="btn btn-secondary btn-sm" onClick={addLine}>+ {t('common.add')}</button>
                </div>
                <div className="overflow-x-auto">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>{t('invoices.description')}</th>
                        <th className="w-20">{t('invoices.quantity')}</th>
                        <th className="w-36">{t('invoices.unitPrice')}</th>
                        <th className="w-28">{t('invoices.vat')}</th>
                        <th className="text-right w-28">{t('common.amount')}</th>
                        <th className="w-10" />
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {lines.map((line, i) => {
                        const lt = lineTotal(line);
                        return (
                          <tr key={i}>
                            <td><input type="text" className="input" value={line.description} onChange={(e) => updateLine(i, 'description', e.target.value)} required /></td>
                            <td><input type="number" min={1} className="input text-center" value={line.quantity} onChange={(e) => updateLine(i, 'quantity', Number(e.target.value))} required /></td>
                            <td><input type="number" step="0.01" className="input text-right" value={line.unitPrice} onChange={(e) => updateLine(i, 'unitPrice', e.target.value)} required /></td>
                            <td>
                              <select className="input min-w-[5rem]" value={line.vatRate} onChange={(e) => updateLine(i, 'vatRate', e.target.value)}>
                                {VAT_RATES.map((r) => <option key={r} value={r}>{r}%</option>)}
                              </select>
                            </td>
                            <td className="text-right font-medium">€{lt.gross.toFixed(2)}</td>
                            <td><button type="button" className="text-red-400 hover:text-red-600" onClick={() => removeLine(i)} disabled={lines.length <= 1}>✕</button></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Notes */}
              <div className="card card-body">
                <label className="label">{t('common.notes')}</label>
                <textarea className="input min-h-[80px]" value={notes} onChange={(e) => setNotes(e.target.value)} />
              </div>
            </div>

            {/* Sidebar totals */}
            <div>
              <div className="card card-body space-y-3 sidebar-sticky">
                <div className="flex justify-between text-sm"><span className="text-gray-500">{t('invoices.subtotal')}</span><span>€{editTotals.net.toFixed(2)}</span></div>
                <div className="flex justify-between text-sm"><span className="text-gray-500">{t('invoices.vat')}</span><span>€{editTotals.vat.toFixed(2)}</span></div>
                <hr />
                <div className="flex justify-between text-lg"><span className="font-bold">{t('invoices.total')}</span><span className="font-bold">€{editTotals.gross.toFixed(2)}</span></div>
                <button type="submit" className="btn btn-primary w-full mt-4" disabled={updateInvoice.isPending}>
                  {updateInvoice.isPending ? t('common.saving') : t('common.save')}
                </button>
                <button type="button" className="btn btn-secondary w-full" onClick={() => navigate(`/invoices/${invoice.id}`)}>
                  {t('common.cancel')}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    );
  }

  // ═══════════════════════════════════════════════
  // VIEW MODE
  // ═══════════════════════════════════════════════
  return (
    <div>
      <div className="flex flex-wrap items-start sm:items-center gap-3 mb-6">
        <button onClick={() => navigate(-1)} className="text-gray-400 hover:text-gray-600">←</button>
        <h1 className="text-2xl font-bold">{invoice.invoiceNumber || `${t('invoices.draftInvoice')}${invoice.client?.name ? ` ${t('common.of')} ${invoice.client.name}` : ''}`}</h1>
        <span className={`badge ${STATUS_COLORS[invoice.status] || 'badge-gray'}`}>
          {t(`invoices.status.${invoice.status}`)}
        </span>
        {invoice.peppolStatus && (
          <span
            className={`badge ${invoice.peppolStatus === 'sent' ? 'badge-blue' : 'badge-gray'}`}
            title={invoice.peppolSentAt ? new Date(invoice.peppolSentAt).toLocaleString() : undefined}
          >
            {t('invoices.peppolStatusLabel')}: {t(`invoices.peppolStatusValue.${invoice.peppolStatus}`, invoice.peppolStatus)}
          </span>
        )}
        <div className="basis-full sm:flex-1" />
        <div className="flex flex-wrap gap-2">
        {!isDraft && (
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => downloadInvoicePdf(invoice.id, `${invoice.invoiceNumber || 'invoice'}.pdf`).catch(() => toast.error(t('common.error')))}
          >
            <svg className="w-4 h-4 mr-1 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            {t('invoices.actions.download')}
          </button>
        )}
        {!isDraft && (
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => openInvoicePdfForPrint(invoice.id).catch(() => toast.error(t('common.error')))}
          >
            <svg className="w-4 h-4 mr-1 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
            </svg>
            {t('invoices.actions.print')}
          </button>
        )}
        {!isDraft && (
          <button
            className="btn btn-secondary btn-sm"
            disabled={sendInvoiceViaPeppol.isPending}
            onClick={() => handleSaveVia('peppol')}
          >
            <svg className="w-4 h-4 mr-1 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75A2.25 2.25 0 016 4.5h12a2.25 2.25 0 012.25 2.25v10.5A2.25 2.25 0 0118 19.5H6a2.25 2.25 0 01-2.25-2.25V6.75z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 7.5l8.25 6 8.25-6" />
            </svg>
            {sendInvoiceViaPeppol.isPending ? t('invoices.peppolSending') : t('invoices.sendViaPeppol')}
          </button>
        )}
        {isDraft && (
          <button className="btn btn-primary btn-sm" onClick={() => navigate(`/invoices/${invoice.id}/edit`)}>
            {t('common.edit')}
          </button>
        )}
        {isDraft && (
          <button
            className="btn btn-success btn-sm"
            disabled={issueInvoice.isPending || sendInvoiceEmail.isPending}
            onClick={handleSaveDraft}
          >
            {issueInvoice.isPending || sendInvoiceEmail.isPending ? t('common.saving') : t('common.save')}
          </button>
        )}
        {transitions.map((s) => (
          <button key={s} className="btn btn-secondary btn-sm" onClick={() => handleStatusChange(s)}>
            → {t(`invoices.status.${s}`)}
          </button>
        ))}
        {['sent', 'viewed', 'partially_paid', 'overdue'].includes(invoice.status) && (
          <button className="btn btn-primary btn-sm" onClick={() => setShowPayment(true)}>
            {t('invoices.recordPayment')}
          </button>
        )}
        {['sent', 'viewed', 'paid', 'partially_paid', 'overdue'].includes(invoice.status) && (
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => {
              setCreditLines(
                invoice.lines?.map((l) => ({
                  description: l.description,
                  quantity: Number(l.quantity),
                  unitPrice: String(l.unitPrice),
                  vatRate: String(Number(l.vatRate)),
                })) || [{ description: '', quantity: 1, unitPrice: '', vatRate: '21' }]
              );
              setCreditReason('');
              setShowCreditNote(true);
            }}
          >
            {t('invoices.actions.createCreditNote')}
          </button>
        )}
                {invoice.status === 'sent' && (
          <button
            className="btn btn-sm bg-emerald-600 hover:bg-emerald-700 text-white"
            onClick={() => {
              setSendLanguage((invoice as any).client?.language || i18n.language?.split('-')[0] || 'en');
              setShowSendModal(true);
            }}
          >
            <svg className="w-4 h-4 mr-1 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            {t('invoices.actions.sendToClient')}
          </button>
        )}
{canCancel && (
          <button
            className="btn btn-danger btn-sm"
            onClick={() => setShowCancelConfirm(true)}
          >
            {t('invoices.actions.cancelInvoice')}
          </button>
        )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Main info */}
        <div className="lg:col-span-3 space-y-6">
          <div className="card card-body">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <span className="text-gray-500">{t('invoices.date')}</span>
                <p className="font-medium">{new Date(invoice.issueDate).toLocaleDateString()}</p>
              </div>
              <div>
                <span className="text-gray-500">{t('invoices.dueDate')}</span>
                <p className="font-medium">{invoice.dueDate ? new Date(invoice.dueDate).toLocaleDateString() : '—'}</p>
              </div>
              <div>
                <span className="text-gray-500">{t('invoices.vatScheme')}</span>
                <p className="font-medium">{invoice.vatScheme}</p>
              </div>
              {invoice.structuredCommunication && (
                <div>
                  <span className="text-gray-500">{t('invoices.communication')}</span>
                  <p className="font-mono font-medium">{invoice.structuredCommunication}</p>
                </div>
              )}
            </div>
          </div>

          {/* Lines */}
          <div className="card">
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>{t('invoices.description')}</th>
                    <th className="text-right">{t('invoices.quantity')}</th>
                    <th className="text-right">{t('invoices.unitPrice')}</th>
                    <th className="text-right">{t('invoices.vat')}</th>
                    <th className="text-right">{t('common.amount')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {invoice.lines?.map((line, i) => (
                    <tr key={i}>
                      <td className="text-gray-900">{line.description}</td>
                      <td className="text-right">{parseFloat(String(line.quantity))}</td>
                      <td className="text-right">€{Number(line.unitPrice).toFixed(2)}</td>
                      <td className="text-right">{Number(line.vatRate)}%</td>
                      <td className="text-right font-medium">€{Number(line.lineTotal).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2">
                    <td colSpan={3} />
                    <td className="text-right text-gray-500">{t('invoices.subtotal')}</td>
                    <td className="text-right font-medium">€{Number(invoice.subtotal).toFixed(2)}</td>
                  </tr>
                  <tr>
                    <td colSpan={3} />
                    <td className="text-right text-gray-500">{t('invoices.vat')}</td>
                    <td className="text-right font-medium">€{Number(invoice.vatAmount).toFixed(2)}</td>
                  </tr>
                  <tr className="text-lg">
                    <td colSpan={3} />
                    <td className="text-right font-bold">{t('invoices.total')}</td>
                    <td className="text-right font-bold">€{Number(invoice.totalAmount).toFixed(2)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* Payments */}
          {invoice.payments && invoice.payments.length > 0 && (
            <div className="card">
              <div className="card-header"><h3 className="font-semibold">{t('invoices.payments')}</h3></div>
              <div className="overflow-x-auto">
                <table className="table">
                  <thead>
                    <tr>
                      <th>{t('invoices.date')}</th>
                      <th>{t('invoices.method')}</th>
                      <th className="text-right">{t('common.amount')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {invoice.payments.map((p) => (
                      <tr key={p.id}>
                        <td>{new Date(p.paidAt).toLocaleDateString()}</td>
                        <td>{p.method}</td>
                        <td className="text-right font-medium text-green-600">€{Number(p.amount).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <div className="card card-body">
            <h3 className="font-semibold mb-3">{t('invoices.client')}</h3>
            <p className="font-medium text-gray-900">{invoice.client?.name}</p>
          </div>

          {invoice.notes && (
            <div className="card card-body">
              <h3 className="font-semibold mb-2">{t('common.notes')}</h3>
              <p className="text-sm text-gray-600 whitespace-pre-wrap">{invoice.notes}</p>
            </div>
          )}
        </div>
      </div>

      {/* Payment modal */}
      {showPayment && (
        <div className="modal-backdrop">
          <form onSubmit={handleRecordPayment} className="card card-body w-full max-w-md space-y-4 modal-sheet">
            <h2 className="text-lg font-bold">{t('invoices.recordPayment')}</h2>
            <div>
              <label className="label">{t('common.amount')}</label>
              <input
                type="number"
                step="0.01"
                className="input"
                value={paymentAmount}
                onChange={(e) => setPaymentAmount(e.target.value)}
                required
              />
            </div>
            <div>
              <label className="label">{t('invoices.method')}</label>
              <select className="input" value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)}>
                <option value="bank_transfer">Bank Transfer</option>
                <option value="cash">Cash</option>
                <option value="card">Card</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div className="flex gap-2 justify-end">
              <button type="button" className="btn btn-secondary" onClick={() => setShowPayment(false)}>
                {t('common.cancel')}
              </button>
              <button type="submit" className="btn btn-primary">{t('common.save')}</button>
            </div>
          </form>
        </div>
      )}

      {/* Credit Note modal */}
      {showCreditNote && (
        <div className="modal-backdrop">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              createCreditNote.mutate(
                {
                  id: invoice!.id,
                  lines: creditLines.map((l) => ({
                    description: l.description,
                    quantity: String(l.quantity),
                    unitPrice: l.unitPrice,
                    vatRate: l.vatRate,
                  })),
                  reason: creditReason || undefined,
                },
                { onSuccess: () => setShowCreditNote(false) }
              );
            }}
            className="card w-full max-w-3xl modal-sheet"
          >
            <div className="card-header">
              <h2 className="text-lg font-bold">{t('invoices.actions.createCreditNote')}</h2>
              <p className="text-sm text-gray-500 mt-1">{t('invoices.creditNote.description')}</p>
            </div>
            <div className="card-body space-y-4">
              <div>
                <label className="label">{t('invoices.creditNote.reason')}</label>
                <input
                  type="text"
                  className="input"
                  value={creditReason}
                  onChange={(e) => setCreditReason(e.target.value)}
                  placeholder={t('invoices.creditNote.reasonPlaceholder')}
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="label mb-0">{t('invoices.lines')}</label>
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() => setCreditLines([...creditLines, { description: '', quantity: 1, unitPrice: '', vatRate: '21' }])}
                  >
                    + {t('common.add')}
                  </button>
                </div>
                <div className="overflow-x-auto border rounded-lg">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>{t('invoices.description')}</th>
                        <th className="w-20">{t('invoices.quantity')}</th>
                        <th className="w-36">{t('invoices.unitPrice')}</th>
                        <th className="w-28">{t('invoices.vat')}</th>
                        <th className="w-10" />
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {creditLines.map((line, i) => (
                        <tr key={i}>
                          <td>
                            <input
                              type="text"
                              className="input"
                              value={line.description}
                              onChange={(e) => setCreditLines(creditLines.map((l, j) => j === i ? { ...l, description: e.target.value } : l))}
                              required
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              min={1}
                              className="input text-center"
                              value={line.quantity}
                              onChange={(e) => setCreditLines(creditLines.map((l, j) => j === i ? { ...l, quantity: Number(e.target.value) } : l))}
                              required
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              step="0.01"
                              className="input text-right"
                              value={line.unitPrice}
                              onChange={(e) => setCreditLines(creditLines.map((l, j) => j === i ? { ...l, unitPrice: e.target.value } : l))}
                              required
                            />
                          </td>
                          <td>
                            <select
                              className="input min-w-[5rem]"
                              value={line.vatRate}
                              onChange={(e) => setCreditLines(creditLines.map((l, j) => j === i ? { ...l, vatRate: e.target.value } : l))}
                            >
                              {VAT_RATES.map((r) => <option key={r} value={r}>{r}%</option>)}
                            </select>
                          </td>
                          <td>
                            <button
                              type="button"
                              className="text-red-400 hover:text-red-600"
                              onClick={() => { if (creditLines.length > 1) setCreditLines(creditLines.filter((_, j) => j !== i)); }}
                              disabled={creditLines.length <= 1}
                            >
                              ✕
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            <div className="card-body border-t flex gap-2 justify-end">
              <button type="button" className="btn btn-secondary" onClick={() => setShowCreditNote(false)}>
                {t('common.cancel')}
              </button>
              <button type="submit" className="btn btn-danger" disabled={createCreditNote.isPending}>
                {createCreditNote.isPending ? t('common.saving') : t('invoices.actions.createCreditNote')}
              </button>
            </div>
          </form>
        </div>
      )}
      {/* Cancel confirmation modal */}
      {showCancelConfirm && (
        <div className="modal-backdrop">
          <div className="card w-full max-w-md">
            <div className="card-header">
              <h2 className="text-lg font-bold">{t('invoices.cancel.title')}</h2>
            </div>
            <div className="card-body">
              <p className="text-gray-600">
                {isDraft
                  ? t('invoices.cancel.draftWarning')
                  : t('invoices.cancel.issuedWarning', { number: invoice.invoiceNumber })}
              </p>
            </div>
            <div className="card-body border-t flex gap-2 justify-end">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowCancelConfirm(false)}
                disabled={cancelInvoice.isPending}
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleCancel}
                disabled={cancelInvoice.isPending}
              >
                {cancelInvoice.isPending
                  ? t('common.saving')
                  : isDraft
                    ? t('invoices.cancel.confirmDelete')
                    : t('invoices.cancel.confirmCancel')}
              </button>
            </div>
          </div>
        </div>
      )}
      {showPaidConfirm && (
        <div className="modal-backdrop">
          <div className="card w-full max-w-md">
            <div className="card-header">
              <h2 className="text-lg font-bold">{t('invoices.paidConfirm.title')}</h2>
            </div>
            <div className="card-body">
              <p className="text-gray-600">{t('invoices.paidConfirm.warning')}</p>
            </div>
            <div className="card-body border-t flex gap-2 justify-end">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowPaidConfirm(false)}
                disabled={updateStatus.isPending}
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={confirmPaidStatus}
                disabled={updateStatus.isPending}
              >
                {updateStatus.isPending ? t('common.saving') : t('invoices.paidConfirm.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}
      {showSaveModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
            <div className="bg-gradient-to-r from-emerald-600 to-teal-600 px-6 py-4">
              <h3 className="text-lg font-semibold text-white">{t('invoices.deliveryOptionsTitle')}</h3>
            </div>
            <div className="px-6 py-5 space-y-4">
              <p className="text-sm text-gray-600">{t('invoices.deliveryOptionsDescription')}</p>
              <button
                className="btn btn-primary w-full"
                onClick={() => handleSaveVia('email')}
                disabled={issueInvoice.isPending || sendInvoiceEmail.isPending}
              >
                {t('invoices.sendViaEmail')}
              </button>
              <button
                className="btn btn-secondary w-full"
                onClick={() => handleSaveVia('whatsapp')}
                disabled={issueInvoice.isPending || sendInvoiceEmail.isPending}
              >
                {t('invoices.sendViaWhatsapp')}
              </button>
              <button
                className="btn btn-secondary w-full"
                onClick={() => handleSaveVia('peppol')}
                disabled={issueInvoice.isPending || sendInvoiceEmail.isPending || sendInvoiceViaPeppol.isPending}
              >
                {sendInvoiceViaPeppol.isPending ? t('invoices.peppolSending') : t('invoices.sendViaPeppol')}
              </button>
            </div>
            <div className="px-6 py-4 bg-gray-50 flex justify-end gap-2 border-t">
              <button className="btn btn-secondary" onClick={() => setShowSaveModal(false)}>
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
      {showSendModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
            <div className="bg-gradient-to-r from-emerald-600 to-teal-600 px-6 py-4">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                {t('invoices.sendInvoiceTitle')}
              </h3>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div>
                <p className="text-sm text-gray-600 mb-3">{t('invoices.sendInvoiceDescription')}</p>
                <div className="bg-gray-50 rounded-lg p-3 text-sm space-y-1">
                  <div className="flex justify-between">
                    <span className="text-gray-500">{t('invoices.client')}</span>
                    <span className="font-medium">{invoice.client?.name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Email</span>
                    <span className="font-medium">{(invoice as any).client?.email || '—'}</span>
                  </div>
                </div>
              </div>
              <div>
                <label className="label">{t('invoices.emailLanguage')}</label>
                <select className="input" value={sendLanguage} onChange={(e) => setSendLanguage(e.target.value)}>
                  {LANGUAGES.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
                </select>
                <p className="text-xs text-gray-400 mt-1">{t('invoices.emailLanguageHint')}</p>
              </div>
            </div>
            <div className="px-6 py-4 bg-gray-50 flex justify-end gap-2 border-t">
              <button className="btn btn-secondary" onClick={() => setShowSendModal(false)}>
                {t('common.cancel')}
              </button>
              <button
                className="btn bg-emerald-600 hover:bg-emerald-700 text-white"
                onClick={() => {
                  sendInvoiceEmail.mutate(
                    { id: invoice!.id, language: sendLanguage },
                    {
                      onSuccess: () => setShowSendModal(false),
                      onError: () => toast.error(t('invoices.sendFailed')),
                    }
                  );
                }}
                disabled={sendInvoiceEmail.isPending}
              >
                {sendInvoiceEmail.isPending ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
                    {t('invoices.sending')}
                  </span>
                ) : (
                  t('invoices.actions.sendToClient')
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
